<template>
  <view-data-container
    :button-title="$t('Risks.Button.New.Title')"
    :loading="isLoadingData"
    :no-data-title="$t('Risks.Message.NoArchiveData.Title')"
  >
    <template v-if="viewHasData">
      <v-row>
        <template
          v-for="(item, i) in pastAnalyses"
        >
          <v-col
            :key="`tab-archive-items-${i}`"
            cols="12"
            lg="4"
            md="6"
            style="padding: 10px"
          >
            <v-lazy
              :key="`lazy-risk-archive-tab-items-${i}`"
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <past-analyses-item-card
                :is-default="item.IsDefault"
                :is-active="item.IsActive"
                :base-currency="item.BaseCurrency"
                :date-created="item.CreatedAtFmt"
                :pie-values="item.PieValues"
                :title="item.Name"
                :title-prefix="$t('NII.Section.PastAnalyses.NIIRisk')"
                :title-prefix-tooltip="$t('NII.Section.PastAnalyses.NIITooltip')"
                :title-value="item.TitleValue || ''"
                :subtitle="item.ValueDateFmt"
                @card-click="onItemCardClick(item)"
                @delete-click="deleteAnalysis(item)"
                @active-click="activeAnalysis(item)"
              />
            </v-lazy>
          </v-col>
        </template>
      </v-row>
      <v-row>
        <v-col v-if="totalPages > 1">
          <v-pagination
            v-model="page"
            color="synthesis-ui-blue-01"
            :length="totalPages"
            total-visible="7"
            @input="handlePageChange"
          />
        </v-col>
      </v-row>
    </template>
  </view-data-container>
</template>

<script>

import PastAnalysesItemCard                 from '@/theme/default/components/common/PastAnalysesItemCard'
import API                                 from '@/api/Api'
import { clone }                           from '@/lib/utils/helper'
import { collect }                         from '@/lib/utils/array'
import RiskPastAnalysesResource            from '@/api/resources/risks/RiskPastAnalysesResource'
import ViewDataContainer                   from '@/theme/default/components/common/ViewDataContainer'

export default {
  name      : 'PastAnalysesList',
  components: {
    ViewDataContainer,
    PastAnalysesItemCard
  },
  directives: {},
  mixins    : [],
  props     : {
    searchTerms: {
      type   : String,
      default: ''
    }
  },
  dataStore: {
    PastAnalyses      : 'PastAnalyses',
    currentAnalysisId : 'ActiveAnalysis.currentAnalysisId',
    previousAnalysisId: 'ActiveAnalysis.previousAnalysisId'
  },
  data () {
    return {
      page           : 1,
      itemsPerPage   : 8,
      totalPages     : 0,
      breadcrumbItems: [{
        text    : this.$t('Risks.Title'),
        disabled: false
      }]
    }
  },
  computed: {
    accountId () {
      return this.user?.selectedAccountId || null
    },
    viewHasData () {
      return this.pastAnalyses && this.pastAnalyses.length > 0
    },
    pastAnalyses () {
      return this.PastAnalyses?.Data || null
    }
  },
  watch: {
    searchTerms (newVal) {
      clearTimeout(this.searchDebounceTimeout)
      this.searchDebounceTimeout = setTimeout(() => {
        this.page = 1
        if (newVal === null || newVal.length > 2 || newVal.length === 0) {
          this.getData()
        }
      }, 500)
    }
  },
  created () {
    this.getData()
  },
  methods: {
    activeAnalysis (item) {
      this.currentAnalysisId = item.Id
      this.getData()
    },
    deleteAnalysis (item) {
      API.Resource.InterestRate.DeleteBankAnalysis(this.accountId, item.Id)
        .then(response => {
          this.handleDeleteAnalysisResponse(response)
        })
        .catch((response) => {
          this.handleDeleteAnalysisResponse(response)
        })
    },
    handleDeleteAnalysisResponse (response) {
      if (API.isResponseSuccess(response)) {
        const result = API.responseData(response)
        const id = result?.deleteBankAnalysis || null
        if (id && this.currentAnalysisId === id) {
          this.currentAnalysisId = null
        }
        this.getData()
      } else {
        this.$bus.$emit('app:error', API.responseErrors(response))
      }
    },
    getRequestParams (accountId, keyword, page, itemsPerPage, analysisId) {
      const params = {}
      if (accountId) {
        params.accountId = accountId
      }
      if (keyword && keyword.trim()) {
        params.keyword = keyword.trim()
      }
      if (page) {
        params.page = page - 1
      }
      if (itemsPerPage) {
        params.itemsPerPage = itemsPerPage
      }

      if (analysisId) {
        params.analysisId = analysisId
      }

      return params
    },

    handlePageChange (value) {
      this.page = value
      this.getData()
    },

    getData () {
      this.isLoadingData = true
      this.analysisId = this.currentAnalysisId || null
      const params = this.getRequestParams(this.accountId, this.searchTerms, this.page, this.itemsPerPage, this.analysisId)

      API.Resource.InterestRate.ListPastAnalyses(params)
        .then(response => {
          this.handleResponse(response)
        })
        .catch(e => {
          this.handleResponse(e.response)
        })
        .finally(() => {
          this.isLoadingData = false
        })
    },

    handleResponse (response) {
      const result = API.responseData(response)

      this.totalPages = result?.listBankAnalyses?.pageCount || 1
      if (this.totalPages < this.page) {
        this.handlePageChange(this.totalPages)
        return
      }
      const data = result?.listBankAnalyses || null
      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        const o = clone(data)

        const activeAnalysis = o.activeAnalysis
          ? new RiskPastAnalysesResource({ ...o.activeAnalysis, active: true })
          : null

        const d = collect(o.analyses, RiskPastAnalysesResource)

        if (activeAnalysis) {
          d.unshift(activeAnalysis)
        }

        this.$set(this.PastAnalyses, 'Data', d)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },

    onItemCardClick (item) {
      this.currentAnalysisId = item.Id
      this.previousAnalysisId = null
      this.$router.push({
        name: 'EVE'
      })
    }

  }
}
</script>

<style scoped>
::v-deep .v-pagination__item, ::v-deep .v-pagination__navigation {
  box-shadow: none !important;
  background-color: transparent !important;
}
::v-deep .v-pagination__item:not(.v-pagination__item--active):hover, ::v-deep .v-pagination__navigation:hover {
  background-color: var(--v-synthesis-ui-blue-07-base) !important;
}
</style>
